import React, { useState, useEffect } from 'react';
import { Button, TextInput, Link } from 'carbon-components-react';
import { FormError, PostalAddress } from 'components';
import { ArrowRight16, Undo16 } from '@carbon/icons-react';
import { connect } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import { CFCProps, Address } from 'types';
import Utils from 'utilities/helper.util';

const mapStateToProps = ({ app }: RootState) => ({
  isLoading: app?.loading
});
const connector = connect(mapStateToProps);
type Props = CFCProps<typeof connector>;

const UpdateUserProfileForm: React.FC<Props | any> = (props) => {
  const { isLoading } = props;
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const [postalAddress, setPostalAddress] = useState<Address>({
    line1: '',
    line2: '',
    locality: '',
    state: '',
    postcode: ''
  });
  const [postalAddressTouched, setPostalAddressTouched] = useState({});
  const [postalAddressError, setPostalAddressError] = useState({});
  const {
    values,
    touched: fTouched,
    errors: fErrors,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    updateValues,
    setFieldValue,
    goToPreviousFormHandler,
    isBYDAMember,
    needPhoneNumber,
    setFieldTouched
  } = props;
  const inputError = ((errors: any, touched: any) => (
    key:
      | 'addressLine1'
      | 'locality'
      | 'state'
      | 'postcode'
      | 'phoneNumber'
      | 'companyName'
  ) => errors[key] && touched[key] && <FormError>{errors[key]}</FormError>)(
    fErrors,
    fTouched
  );

  const updateAddress = (address: any, type: any) => {
    if (address.line1 !== postalAddress.line1) {
      setFieldValue('addressLine1', address.line1);
    }

    if (address.line2 !== postalAddress.line2) {
      setFieldValue('addressLine2', address.line2);
    }

    if (address.locality !== postalAddress.locality) {
      setFieldValue('locality', address.locality);
    }

    if (address.state !== postalAddress.state) {
      setFieldValue('state', address.state);
    }

    if (address.postcode !== postalAddress.postcode) {
      setFieldValue('postcode', address.postcode);
    }

    if (type === 'fullAddress') {
      setTimeout(() => {
        updateTouched('fullAddress');
      }, 100);
    }
  };

  const updateTouched = (key: string) => {
    if (key === 'line1') {
      setFieldTouched('addressLine1');
    } else if (key === 'line2') {
      setFieldTouched('addressLine2');
    } else {
      setFieldTouched(key);
    }
  };

  useEffect(() => {
    const { addressLine1, addressLine2, locality, postcode, state } = fTouched;
    setPostalAddressTouched({
      line1: addressLine1,
      line2: addressLine2,
      locality,
      state,
      postcode
    });
  }, [fTouched]);

  useEffect(() => {
    const { addressLine1, addressLine2, locality, postcode, state } = fErrors;
    setPostalAddressError({
      line1: addressLine1,
      line2: addressLine2,
      locality,
      state,
      postcode
    });
  }, [fErrors]);

  useEffect(() => {
    const {
      phoneNumber,
      addressLine1,
      addressLine2,
      locality,
      postcode,
      state
    } = values;
    const requiredFields = needPhoneNumber
      ? { phoneNumber, addressLine1, locality, postcode, state }
      : { addressLine1, locality, postcode, state };
    const isObjPropsEmpty: boolean = Utils.isObjectPropertiesEmpty(
      requiredFields
    );
    setIsButtonDisabled(isObjPropsEmpty);
    updateValues(values);
    setPostalAddress({
      line1: addressLine1,
      line2: addressLine2,
      locality,
      state,
      postcode
    });
  }, [values, updateValues, needPhoneNumber]);

  return (
    <form
      onSubmit={handleSubmit}
      className="lodgeEnquiryFrom custom-scroller step4"
    >
      <div className="lodgeEnquiryFrom--body">
        <div className="update-user--desc">
          We need your {needPhoneNumber ? 'phone number and ' : ''}postal
          address to complete the process. You only need to provide this once
          and can change it in your profile.
        </div>
        {needPhoneNumber && (
          <section className="formFieldItem">
            <TextInput
              id="phoneNumber"
              name="phoneNumber"
              data-testid="phoneNumber"
              labelText="Phone"
              onChange={handleChange}
              onBlur={handleBlur}
              defaultValue={values.phoneNumber}
            />
            {inputError('phoneNumber')}
          </section>
        )}
        {!isBYDAMember && (
          <section className="formFieldItem">
            <TextInput
              id="companyName"
              name="companyName"
              labelText="Company name (optional)"
              onBlur={handleBlur}
              onChange={handleChange}
              maxLength={100}
            />
            {inputError('companyName')}
          </section>
        )}
        <PostalAddress
          defaultAddress={postalAddress}
          onChangeAddress={updateAddress}
          touched={postalAddressTouched}
          errors={postalAddressError}
          onBlurAddress={updateTouched}
        />
      </div>
      <section className="button--wrapper">
        <Button
          className="cancelEnquiryButton"
          kind="secondary"
          onClick={goToPreviousFormHandler}
          renderIcon={Undo16}
          iconDescription="Previous"
          hasIconOnly
          tooltipPosition="bottom"
          tooltipAlignment="start"
        />
        <Button
          className="submitForm"
          type="submit"
          kind={!fErrors ? 'danger' : 'primary'}
          renderIcon={ArrowRight16}
          disabled={
            isButtonDisabled ||
            isSubmitting ||
            isLoading ||
            Object.keys(fErrors).length !== 0
          }
          data-testid="submit"
          onClick={handleSubmit}
        >
          {'Lodge enquiry'}
        </Button>
        <div className="recaptcha-disclaimer">
          This site is protected by reCAPTCHA and the Google{' '}
          <Link href="https://policies.google.com/privacy" target="_blank">
            Privacy Policy{' '}
          </Link>{' '}
          and{' '}
          <Link href="https://policies.google.com/terms" target="_blank">
            Terms of Service
          </Link>{' '}
          apply.
        </div>
      </section>
    </form>
  );
};

UpdateUserProfileForm.displayName = 'UpdateUserProfileForm';

export default connector(UpdateUserProfileForm);
